export const buildAccountSearchParams = ({
  formValues = {},
  page = 1,
  pageSize = 20,
  normalizeOptionalString,
  operatorId = '',
}) => {
  const params = {
    page,
    page_size: pageSize,
    keyword: normalizeOptionalString(formValues.keyword) || '',
    operator_id: normalizeOptionalString(operatorId),
    filters: {},
    sort: { field: 'created_at', order: 'desc' },
  };

  if (Array.isArray(formValues.platforms) && formValues.platforms.length > 0) {
    params.filters.platforms = formValues.platforms;
  }

  if (Array.isArray(formValues.status) && formValues.status.length > 0) {
    params.filters.status = formValues.status;
  }

  if (normalizeOptionalString(formValues.operator_user_id)) {
    params.filters.operator_user_id = normalizeOptionalString(formValues.operator_user_id);
  }

  if (Object.keys(params.filters).length === 0) delete params.filters;
  if (!params.operator_id) delete params.operator_id;
  return params;
};

export const createLoadAccountDataHandler = ({
  paginationRef,
  setLoading,
  searchPublishAccounts,
  recentlyUpdatedAccountsRef,
  setDataSource,
  setPagination,
  message,
  resolveErrorMessage,
}) => async (searchParams = {}, page = null, pageSize = null) => {
  setLoading(true);
  try {
    const currentPage = page !== null ? page : paginationRef.current.current;
    const currentPageSize = pageSize !== null ? pageSize : paginationRef.current.pageSize;

    const params = {
      ...searchParams,
      page: currentPage,
      page_size: currentPageSize,
    };

    const response = await searchPublishAccounts(params);
    if (response.status === 'success') {
      const ttlMs = 15000;
      const now = Date.now();
      for (const [id, meta] of recentlyUpdatedAccountsRef.current.entries()) {
        if (!meta?.ts || now - meta.ts > ttlMs) recentlyUpdatedAccountsRef.current.delete(id);
      }

      const list = Array.isArray(response.items) ? response.items : [];
      const merged = list.map((item) => {
        const accountId = item?.account_id;
        if (!accountId) return item;
        const meta = recentlyUpdatedAccountsRef.current.get(accountId);
        if (!meta?.patch) return item;
        return { ...item, ...meta.patch };
      });

      setDataSource(merged);
      setPagination({
        current: response.page || currentPage,
        pageSize: response.page_size || currentPageSize,
        total: response.total || 0,
      });
    } else {
      message.error(resolveErrorMessage(response.error_message, '加载账号列表失败'));
    }
  } catch (error) {
    console.error('加载账号列表失败:', error);
    message.error('加载账号列表失败，请稍后重试');
  } finally {
    setLoading(false);
  }
};

export const createReloadCurrentAccountListHandler = ({
  paginationRef,
  form,
  buildSearchParams,
  loadData,
}) => async () => {
  const current = paginationRef.current || { current: 1, pageSize: 20 };
  const formValues = form.getFieldsValue();
  const searchParams = buildSearchParams(formValues, current.current, current.pageSize);
  await loadData(searchParams, current.current, current.pageSize);
};

export const createAccountSearchHandlers = ({
  form,
  pagination,
  setSelectedAccountKeys,
  buildSearchParams,
  loadData,
}) => ({
  handleSearch: () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, 1, pagination.pageSize);
    setSelectedAccountKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  },
  handleFilterFinish: (values) => {
    const searchParams = buildSearchParams(values, 1, pagination.pageSize);
    setSelectedAccountKeys([]);
    loadData(searchParams, 1, pagination.pageSize);
  },
});

const createReloadAfterDelete = ({
  dataSource,
  pagination,
  form,
  buildSearchParams,
  loadData,
}) => (deletedAccountIds = []) => {
  const ids = Array.from(new Set((deletedAccountIds || []).filter(Boolean)));
  const currentPageIds = (dataSource || []).map((item) => item.account_id).filter(Boolean);
  const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
  const shouldBackPage = pagination.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
  const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

  const formValues = form.getFieldsValue();
  const searchParams = buildSearchParams(formValues, nextPage, pagination.pageSize);
  loadData(searchParams, nextPage, pagination.pageSize);
};

export const createAccountDeleteHandlers = ({
  selectedAccountKeys,
  dataSource,
  pagination,
  form,
  buildSearchParams,
  loadData,
  Modal,
  message,
  deletePublishAccounts,
  resolveErrorMessage,
  setSelectedAccountKeys,
}) => {
  const reloadListAfterDelete = createReloadAfterDelete({
    dataSource,
    pagination,
    form,
    buildSearchParams,
    loadData,
  });

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除账号 "${record.account_name || record.account_id}" 吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: '正在删除...', key: 'deleteAccount' });
          const response = await deletePublishAccounts(record.account_id);
          if (response.status === 'success') {
            const deletedCount = Array.isArray(response.deleted) ? response.deleted.length : 0;
            const failedCount = Array.isArray(response.failed) ? response.failed.length : 0;
            if (failedCount === 0) {
              message.success({ content: `删除成功（${deletedCount}）`, key: 'deleteAccount' });
            } else {
              message.warning({ content: `删除完成：成功 ${deletedCount}，失败 ${failedCount}`, key: 'deleteAccount' });
            }
            setSelectedAccountKeys((prev) => (prev || []).filter((id) => id !== record.account_id));
            reloadListAfterDelete([record.account_id]);
          } else {
            message.error({ content: resolveErrorMessage(response.error_message, '删除失败'), key: 'deleteAccount' });
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error({ content: '删除失败，请稍后重试', key: 'deleteAccount' });
        }
      },
    });
  };

  const handleBatchDelete = () => {
    if (!selectedAccountKeys.length) {
      message.warning('请先选择要删除的账号');
      return;
    }

    const accountIds = [...selectedAccountKeys];
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${accountIds.length} 个账号吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: `正在删除 ${accountIds.length} 个账号...`, key: 'batchDeleteAccount' });
          const response = await deletePublishAccounts(accountIds);
          if (response.status === 'success') {
            const deletedIds = Array.isArray(response.deleted) ? response.deleted : [];
            const failedItems = Array.isArray(response.failed) ? response.failed : [];
            const deletedCount = deletedIds.length;
            const failedCount = failedItems.length;

            setSelectedAccountKeys([]);

            if (failedCount === 0) {
              message.success({ content: `成功删除 ${deletedCount} 个账号`, key: 'batchDeleteAccount' });
            } else {
              message.warning({ content: `删除完成：成功 ${deletedCount}，失败 ${failedCount}`, key: 'batchDeleteAccount', duration: 5 });
            }

            reloadListAfterDelete(accountIds);
          } else {
            message.error({ content: resolveErrorMessage(response.error_message, '批量删除失败'), key: 'batchDeleteAccount' });
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error({ content: '批量删除失败，请稍后重试', key: 'batchDeleteAccount' });
        }
      },
    });
  };

  return {
    handleDelete,
    handleBatchDelete,
  };
};
